import Link from 'next/link'
import Image from 'next/image'
import BlogPost from '@interfaces/post'
import { format } from 'date-fns'
import { blogCategories } from '../../blog.config'

export type CardVariant = 'featured' | 'regular'

interface BlogCardProps {
  locale: string
  post: BlogPost
  variant?: CardVariant
}

export default function BlogCard({
  locale,
  post,
  variant = 'regular',
}: BlogCardProps) {
  const dateString = post.date
    ? format(new Date(post.date), 'd MMM yyyy')
    : ''

  const category =
    post.category &&
    blogCategories.find(({ slug }) => slug === post.category)?.title

  if (variant === 'featured') {
    return (
      <Link
        href={`/blog/${post.slug}`}
        locale={locale}
        className="group block overflow-hidden rounded-3xl border border-line-subtle bg-surface shadow-card transition duration-300 ease-smooth hover:-translate-y-1 hover:shadow-card-hover"
      >
        <div className="grid lg:grid-cols-[1.2fr_1fr]">
          {post.image && (
            <div className="relative aspect-[16/10] overflow-hidden bg-surface-muted lg:aspect-auto">
              <Image
                src={post.image}
                alt={post.title}
                fill
                sizes="(max-width: 1024px) 100vw, 60vw"
                className="object-cover transition duration-500 ease-smooth group-hover:scale-105"
              />
            </div>
          )}
          <div className="flex flex-col justify-center p-8 lg:p-12">
            <div className="flex items-center gap-3 text-eyebrow font-semibold uppercase">
              {category && (
                <span className="text-brand-700">{category}</span>
              )}
              {dateString && (
                <>
                  <span className="text-ink-muted">·</span>
                  <span className="text-ink-tertiary">{dateString}</span>
                </>
              )}
              {post.read_time && (
                <>
                  <span className="text-ink-muted">·</span>
                  <span className="text-ink-tertiary">
                    {post.read_time} min read
                  </span>
                </>
              )}
            </div>
            <h2 className="mt-4 text-display-md font-medium text-ink-primary tracking-[-0.025em]">
              {post.title}
            </h2>
            {post.summary && (
              <p className="mt-4 text-body-lg text-ink-secondary line-clamp-3">
                {post.summary}
              </p>
            )}
            <div className="mt-8 inline-flex items-center gap-2 text-body-md font-medium text-brand-900 transition-transform group-hover:translate-x-1">
              Read article
              <span aria-hidden>→</span>
            </div>
          </div>
        </div>
      </Link>
    )
  }

  return (
    <Link
      href={`/blog/${post.slug}`}
      locale={locale}
      className="group flex flex-col overflow-hidden rounded-2xl border border-line-subtle bg-surface shadow-card transition duration-300 ease-smooth hover:-translate-y-1 hover:shadow-card-hover"
    >
      {post.image && (
        <div className="relative aspect-[16/10] overflow-hidden bg-surface-muted">
          <Image
            src={post.image}
            alt={post.title}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover transition duration-500 ease-smooth group-hover:scale-105"
          />
        </div>
      )}
      <div className="flex flex-grow flex-col p-6 lg:p-7">
        <div className="flex items-center gap-2 text-caption font-semibold uppercase tracking-[0.12em]">
          {category && <span className="text-brand-700">{category}</span>}
          {dateString && (
            <>
              {category && <span className="text-ink-muted">·</span>}
              <span className="text-ink-tertiary">{dateString}</span>
            </>
          )}
        </div>
        <h3 className="mt-3 text-display-sm font-medium text-ink-primary line-clamp-2">
          {post.title}
        </h3>
        {post.summary && (
          <p className="mt-3 flex-grow text-body-md text-ink-secondary line-clamp-3">
            {post.summary}
          </p>
        )}
        {post.read_time && (
          <div className="mt-5 text-body-sm text-ink-tertiary">
            {post.read_time} min read
          </div>
        )}
      </div>
    </Link>
  )
}
